import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiRequest } from '../utils/apiClient';
import { useAuth } from '../hooks/useAuth';
import type { TeamRole } from '../types/company';

export interface MyDeveloper {
  id: string;
  name: string;
  nameAr?: string | null;
  slug: string;
  logoUrl?: string | null;
  coverUrl?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  headquartersCity?: string | null;
  foundedYear?: number | null;
  licenseNumber?: string | null;
  isVerified?: boolean;
  myRole?: TeamRole;
}

interface DeveloperContextValue {
  developer: MyDeveloper | null;
  myRole: TeamRole | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const DeveloperContext = createContext<DeveloperContextValue | undefined>(undefined);

export function DeveloperProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [developer, setDeveloper] = useState<MyDeveloper | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!user) { setDeveloper(null); setIsLoading(false); return; }
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiRequest<{ data: MyDeveloper | null }>('/developers/my/profile');
      setDeveloper(res.data ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load developer');
      setDeveloper(null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => { refetch(); }, [refetch]);

  const value = useMemo<DeveloperContextValue>(() => ({
    developer,
    myRole: developer?.myRole ?? null,
    isLoading,
    error,
    refetch,
  }), [developer, isLoading, error, refetch]);

  return <DeveloperContext.Provider value={value}>{children}</DeveloperContext.Provider>;
}

export function useDeveloper(): DeveloperContextValue {
  const ctx = useContext(DeveloperContext);
  if (!ctx) throw new Error('useDeveloper must be used within a DeveloperProvider');
  return ctx;
}
